import { Photo } from "@/core/photo"
import {
	cleanupWaveletTextures,
	denoiseTextureWaveletChunked,
	initDenoiseTextures,
} from "@/core/utils/denoise"
import { IOAdapter, IOAdapterOptions } from "@/core/io-adapter"
import { getDefaultAdjustments } from "@/core/adjustments"
import { hasExtension } from "@/storage/file-system"
import { Renderer } from "@/core/pipeline/renderer"
import { DenoiseTextures } from "@/core/pipeline/passes/wavelet-denoise-pass"
import {
	blitChunkTextureIntoFinalTexture,
	blitDenoiseChunkTexturesIntoFinalTextures,
	CHUNK_PADDING,
	CHUNK_SIZE,
} from "../raw/demosaic/chunking-utils"
import { vec2 } from "gl-matrix"
import { bitmapAdapterExtensions } from "./bitmap-adapter-extensions"

export class BitmapIOAdapter extends IOAdapter {
	public static supportedExtensions = bitmapAdapterExtensions

	public getName(): string {
		return "bitmap-adapter"
	}

	public canLoad(file: File): boolean {
		return hasExtension(file, BitmapIOAdapter.supportedExtensions)
	}

	public async load(file: File, renderer: Renderer, options: IOAdapterOptions): Promise<void> {
		/* Defaults */

		const defaultAdjustments = getDefaultAdjustments(file)
		const denoiseEnabled = options.enableDenoise ?? true
		const adjustments = options.adjustments ?? defaultAdjustments

		renderer.performanceCounters.beginLogicalBlock("render-bitmap")
		renderer.performanceCounters.begin("render-bitmap-texture")

		/* Loading bitmap texture via our own wasm library */

		const bitmap = await createImageBitmap(file, {
			colorSpaceConversion: "default",
		})

		const scale = options.enableHalfResolution ? 2 : 1

		options.signal?.throwIfAborted()

		renderer.sourceTexture.update({
			format: "rgba8unorm",
			data: bitmap,
		})

		/* Processing loaded texure with a shader */

		const originalImageSize: vec2 = [bitmap.width, bitmap.height]

		const outputWidth = Math.floor(bitmap.width / scale)
		const outputHeight = Math.floor(bitmap.height / scale)

		const outputTexture = renderer.inputTexture

		renderer.inputTexture.update({
			format: renderer.backend.textureFormat,
			data: null,
			width: outputWidth,
			height: outputHeight,
		})

		// Preparing chunk textures

		const totalChunkSize = CHUNK_SIZE + 2 * CHUNK_PADDING

		const chunkDenoiseTextures = renderer.chunkDenoiseTextures

		let outputDenoiseTextures: DenoiseTextures | null = null

		if (options.enableDenoise) {
			outputDenoiseTextures = initDenoiseTextures(renderer)

			outputDenoiseTextures.denoiseLumTexture.updateSize(outputWidth, outputHeight)
			outputDenoiseTextures.denoiseColorTexture.updateSize(
				Math.floor((outputWidth + 1) / 2),
				Math.floor((outputHeight + 1) / 2),
			)

			chunkDenoiseTextures.denoiseLumTexture.updateSize(totalChunkSize, totalChunkSize)
			chunkDenoiseTextures.denoiseColorTexture.updateSize(
				totalChunkSize / 2.0,
				totalChunkSize / 2.0,
			)
		}

		// Chunked denoise textures

		const buffers = renderer.chunkingBuffers
		buffers.updateSize(totalChunkSize, totalChunkSize)

		// Framebuffers

		const gl = renderer.backend.gl

		const fboRead = gl.createFramebuffer()
		const fboDraw = gl.createFramebuffer()

		if (!fboRead || !fboDraw) {
			throw new Error("Cannot demosaic Bayer pattern photos due to internal error.")
		}

		gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, fboDraw)
		gl.framebufferTexture2D(
			gl.DRAW_FRAMEBUFFER,
			gl.COLOR_ATTACHMENT0,
			gl.TEXTURE_2D,
			outputTexture.nativeTexture,
			0,
		)

		gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null)

		const nX = Math.ceil(outputWidth / CHUNK_SIZE)
		const nY = Math.ceil(outputHeight / CHUNK_SIZE)

		for (let yi = 0; yi < nY; yi++) {
			for (let xi = 0; xi < nX; xi++) {
				const x0 = xi * CHUNK_SIZE,
					y0 = yi * CHUNK_SIZE
				const x0r = Math.floor((x0 - CHUNK_PADDING) * scale)
				const y0r = Math.floor((y0 - CHUNK_PADDING) * scale)

				renderer.sourceTexture.bind(0)
				buffers.writeBuffer.bindFramebuffer()

				renderer.backend.shaders.bitmap.render({
					source: 0,
					offset: [x0r, y0r],
					scale,
				})

				// Denoise if enabled

				if (options.enableDenoise && outputDenoiseTextures) {
					denoiseTextureWaveletChunked(
						renderer,
						buffers.writeBuffer,
						chunkDenoiseTextures,
						{
							iso: options.metadata?.iso ?? 400,
							shutterSpeed: options.metadata?.shutterSpeed ?? 0.01,
							isJpeg: false,
						},
					)

					// Copying denoise texture
					blitDenoiseChunkTexturesIntoFinalTextures(
						gl,
						fboDraw,
						fboRead,
						chunkDenoiseTextures,
						outputDenoiseTextures,
						[x0, y0],
					)
				}

				// Rendering demosaiced chunk onto the final large texure

				blitChunkTextureIntoFinalTexture(
					gl,
					fboDraw,
					fboRead,
					buffers.writeBuffer,
					outputTexture,
					[x0, y0],
				)
			}
		}

		gl.deleteFramebuffer(fboRead)
		gl.deleteFramebuffer(fboDraw)

		renderer.inputTexture.generateMipmap()

		renderer.denoiseEnabled = denoiseEnabled

		renderer.profileManager.setProfile(renderer.photo, null)
		renderer.profileManager.setLensProfile()
		renderer.profileManager.setEmbeddedProfile(null)

		bitmap.close()

		renderer.sourceTexture.unbind(0)
		renderer.sourceTexture.reset()

		renderer.performanceCounters.end("render-bitmap-texture")
		renderer.performanceCounters.endLogicalBlock("render-bitmap")

		renderer.dualBuffers.reset()
		renderer.originalDualBuffers.reset()
		renderer.maskBuffers.reset()
		renderer.blurBuffers.reset()

		cleanupWaveletTextures(renderer)

		renderer.photo = new Photo({
			id: options.photoId,
			type: "bitmap",
			texture: renderer.inputTexture,
			adjustments,
			originalImageSize,
		})
	}
}
